import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiUrl, getAuthHeaders, getAuthHeadersForFormData } from '../../config/api.config';

const initialState = {
  events: [],
  currentEvent: null,
  loading: false,
  error: null,
  success: false,
  totalPages: 1,
  currentPage: 1
};

// Get all events
export const getAllEvents = createAsyncThunk(
  'events/getAll',
  async ({ page = 1, limit = 10, category, upcoming } = {}, { rejectWithValue }) => {
    try {
      let url = `/events?page=${page}&limit=${limit}`;
      if (category) url += `&category=${category}`;
      if (upcoming) url += `&upcoming=true`;
      
      const response = await axios.get(getApiUrl(url));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch events'
      );
    }
  }
);

// Get single event
export const getEvent = createAsyncThunk(
  'events/getOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(getApiUrl(`/events/${id}`));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch event'
      );
    }
  }
);

// Create event (Admin)
export const createEvent = createAsyncThunk(
  'events/create',
  async (eventData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        getApiUrl('/events'),
        eventData,
        { headers: getAuthHeadersForFormData() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create event'
      );
    }
  }
);

// Update event (Admin)
export const updateEvent = createAsyncThunk(
  'events/update',
  async ({ id, eventData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        getApiUrl(`/events/${id}`),
        eventData,
        { headers: getAuthHeadersForFormData() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update event'
      );
    }
  }
);

// Delete event (Admin)
export const deleteEvent = createAsyncThunk(
  'events/delete',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(
        getApiUrl(`/events/${id}`),
        { headers: getAuthHeaders() }
      );
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete event'
      );
    }
  }
);

const eventSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    clearCurrentEvent: (state) => {
      state.currentEvent = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get all events
      .addCase(getAllEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload.data.events;
        state.totalPages = action.payload.data.totalPages || 1;
        state.currentPage = action.payload.data.currentPage || 1;
      })
      .addCase(getAllEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get single event
      .addCase(getEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEvent = action.payload.data.event;
      })
      .addCase(getEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create event
      .addCase(createEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.events.unshift(action.payload.data.event);
        state.success = true;
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update event
      .addCase(updateEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.events.findIndex(e => e._id === action.payload.data.event._id);
        if (index !== -1) {
          state.events[index] = action.payload.data.event;
        }
        state.success = true;
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete event
      .addCase(deleteEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.events = state.events.filter(e => e._id !== action.payload);
        state.success = true;
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearSuccess, clearCurrentEvent } = eventSlice.actions;
export default eventSlice.reducer;
